import { useSelector } from 'react-redux';

import { useIsSignedIn } from '@front/auth/src/authentication-context';

import { isRecurringCleaningSelector } from '@front/common/src/ducks/customer/basket';
import { getUniverseRef } from '@front/common/src/ducks/customer/basket-selectors';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { isCleaningReference } from '@front/common/src/utils/universes';

import { CONFIG_BY_COUNTRY } from '../constants/country-config';
import { useGetCesuWalletQuery } from '../queries/cesu-queries';

type UseHasCesuWalletParams = {
  enabled?: boolean;
};

// ITC is replaced by CESU in the cleaning funnel when the customer has money in the CESU wallet for spot cleaning.
// If the CESU could not load, we keep ITC; isCesuWalletLoading lets one-shot flows wait for the resolved value.
// WARNING: CESU is web-only for the MVP. mobile can't use it because of the different auth mechanism
export const useHasCesuWallet = ({ enabled = true }: UseHasCesuWalletParams = {}) => {
  const isSignedIn = useIsSignedIn();
  const universeReference = useSelector(getUniverseRef);
  const isRecurringCleaning = useSelector(isRecurringCleaningSelector);
  const { isCesuAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const shouldFetchCesuWallet =
    enabled && isSignedIn && isCesuAvailable && isCleaningReference(universeReference) && !isRecurringCleaning;

  const { cesuAmount, isLoadingCesuAmount } = useGetCesuWalletQuery({
    enabled: shouldFetchCesuWallet,
    universe: universeReference,
  });

  return {
    hasCesuWallet: shouldFetchCesuWallet && (cesuAmount ?? 0) > 0,
    // A disabled query reports isLoading forever in react-query v4, hence the enabled guard.
    isCesuWalletLoading: shouldFetchCesuWallet && isLoadingCesuAmount,
  };
};
